import React, { useCallback } from 'react';

import { WpModal, WpModalBody, WpModalFooter, WpModalHeader } from 'Wpds/components/modal';
import { WpButton, WpButtonGroup } from 'Wpds/components/button';

// Hooks
import { useApp } from 'Shared/utils/hooks/use-app';
import { useTranslation } from 'Shared/utils/hooks/use-translation';

const STOPPED = 'stopped';

type Props = {
  localVideoURL: string;
  captureMicrophone: boolean;
  frameGrab: string;
  recordedBlobs;
  closeVideoRecorder(): void;
  setRecordingStatus: Function;
  onSave?: Function;
  fetchStream: Function;
};

function VideoRecorderModal({
  closeVideoRecorder,
  setRecordingStatus,
  onSave,
  recordedBlobs,
  frameGrab,
  fetchStream,
  captureMicrophone,
  localVideoURL,
}: Props) {
  const { appId } = useApp();
  const { tr } = useTranslation();

  const saveRecording = useCallback(() => {
    const recordedFile = new Blob(recordedBlobs, { type: captureMicrophone ? 'audio/webm' : 'video/webm' });
    // eslint-disable-next-line dot-notation
    recordedFile['name'] = 'wpVideoRecording';
    if (onSave) onSave({ file: recordedFile, preview: frameGrab, audioOnly: captureMicrophone, url: localVideoURL });
    (window as any).analytics.track('Saved Recording', { app_id: appId });

    closeVideoRecorder();
  }, [appId, captureMicrophone, closeVideoRecorder, frameGrab, onSave, recordedBlobs, localVideoURL]);

  const discardRecording = useCallback(() => {
    setRecordingStatus(STOPPED);
    (window as any).analytics.track('Discarded Recording', { app_id: appId });
    fetchStream();
  }, [appId, fetchStream, setRecordingStatus]);

  return (
    <WpModal size="lg" isOpen onToggle={closeVideoRecorder} dismissOnBackdrop={false}>
      <WpModalHeader>
        {captureMicrophone
          ? tr('perform.video.recorder_modal.header.add_audio')
          : tr('perform.video.recorder_modal.header.add_video')}
      </WpModalHeader>
      <WpModalBody>
        {captureMicrophone && <audio autoPlay loop controls style={{ width: '100%' }} src={localVideoURL} />}
        {!captureMicrophone && <video autoPlay loop controls width="100%" src={localVideoURL} />}
      </WpModalBody>
      <WpModalFooter>
        <WpButtonGroup>
          <WpButton variant="tertiary" onClick={closeVideoRecorder}>
            {tr('perform.video.recorder_modal.button.cancel')}
          </WpButton>
          <WpButton variant="secondary" onClick={discardRecording}>
            {tr('perform.video.recorder_modal.button.re_record')}
          </WpButton>
          <WpButton onClick={saveRecording}>{tr('perform.video.recorder_modal.button.save_and_add')}</WpButton>
        </WpButtonGroup>
      </WpModalFooter>
    </WpModal>
  );
}

export default VideoRecorderModal;
